import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  validateFormFieldsAndSubmit,
} from 'client/helpers/formikHelpers';

import {
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
} from 'common/listingConstants';

import { SR_TAG_THEME_GOLD_WHITE, SR_TAG_THEME_BLUE_WHITE } from 'client/components/SrTag/SrTag';
import SrCard, { SR_CARD_FONT_SIZE_SMALL } from 'client/components/SrCard/SrCard';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';

import SrPartnerBankAccountNew from './SrPartnerBankAccountNew';
import SrPartnerBankAccountConnected from './SrPartnerBankAccountConnected';

export const getPartnerListingFeeSettingsFormFields = ({ values }) => {
  return [
    {
      id: 'whoCollectsSecurityDepositPetFees',
      type: 'info_checkbox',
      validations: ['required'],
      properties: {
        choices: [
          {
            label: 'Sunroom EasyPay™️',
            description:
              'Sunroom collects the renters funds online & ' +
              'electronically deposits them to the bank account you choose below.',
            tagContents: 'Recommended',
            tagTheme: SR_TAG_THEME_GOLD_WHITE,
            value: LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM,
          },
          {
            label: 'Alternative Method',
            description:
              'You’ll write the custom instructions for how your client wants the payment. ' +
              'Sunroom will relay these instructions to the renter.',
            tagContents: 'Manual',
            tagTheme: SR_TAG_THEME_BLUE_WHITE,
            value: LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
          },
        ],
      },
    },
    showFieldIf(
      values.whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
      {
        id: 'securityDepositCollectionInstructions',
        type: 'long_text',
        validations: ['required'],
        title: 'Payment Instructions',
        subtitle:
          'Provide details about your client’s account on Zelle, ' +
          'Venmo, or other services to expedite this process.',
        properties: {
          placeholder: 'Write here...',
        },
      },
    ),
    {
      id: 'srStripeAccountId',
      type: 'hidden',
    },
  ];
};

const SrPartnerBankAccount = ({
  onSubmit,
  stripeAccount,
  listing,
  partner,
  pendingStripeCharge,
  hideAddBankAccount,
  alternativeMethodEnabled,
  whoCollectsSecurityDepositPetFees,
  securityDepositCollectionInstructions,
}) => {
  const defaultValues = {
    whoCollectsSecurityDepositPetFees,
    securityDepositCollectionInstructions,
    srStripeAccountId: stripeAccount?.id,
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getPartnerListingFeeSettingsFormFields({
          values: defaultValues,
        }),
        defaultValues,
      })}
      validate={(values) => {
        return createFormValidationFn(
          getPartnerListingFeeSettingsFormFields({
            values,
          }),
        )(values);
      }}
      onSubmit={(values) => {
        const payload = { ...values };

        if (
          (listing?.collectFirstMonth || listing?.collectLastMonthRent) &&
          whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL &&
          values.whoCollectsSecurityDepositPetFees ===
            LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL
        ) {
          global.alert(
            'You can not collect the security deposit and rent if ' +
              'you are using an alternative collection method.',
          );
          return;
        }

        if (
          payload.whoCollectsSecurityDepositPetFees ===
          LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM
        ) {
          payload.securityDepositCollectionInstructions = null;
        } else {
          payload.srStripeAccountId = null;
        }

        onSubmit(payload);
      }}
      render={(formikBag) => {
        const formFields = getPartnerListingFeeSettingsFormFields({
          values: formikBag.values,
        });
        const getPartnerListingFeeSettingsFormFieldsById = createFormFieldsByIdGetter(formFields);

        const isSunroomWhoCollectsSecurityDeposit =
          formikBag.values.whoCollectsSecurityDepositPetFees !==
          LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL; // eslint-disable-line max-len

        return (
          <Form className="form">
            {alternativeMethodEnabled && (
              <SrCard headerSizeType={SR_CARD_FONT_SIZE_SMALL} header="Choose How to Collect">
                <SrFormikField
                  formField={getPartnerListingFeeSettingsFormFieldsById(
                    'whoCollectsSecurityDepositPetFees',
                  )}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
                <SrBox height="20px" />
                <SrFormikField
                  formField={getPartnerListingFeeSettingsFormFieldsById(
                    'securityDepositCollectionInstructions',
                  )}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </SrCard>
            )}

            {isSunroomWhoCollectsSecurityDeposit && stripeAccount && (
              <SrPartnerBankAccountConnected
                listingId={listing?.id}
                partnerId={partner?.id}
                stripeAccount={stripeAccount}
                pendingStripeCharge={pendingStripeCharge}
              />
            )}
            {isSunroomWhoCollectsSecurityDeposit && !hideAddBankAccount && (
              <SrPartnerBankAccountNew listing={listing} partner={partner} />
            )}
            {alternativeMethodEnabled && (
              <SrStaticFooter>
                <SrText
                  display="block"
                  lineHeight="25px"
                  font="greycliff"
                  color="grey600"
                  fontSize="13px"
                  weight="500"
                >
                  Updates can take 24 hours to syndicate
                </SrText>
                <SrFooterSaveButton
                  isSubmitting={false}
                  onClick={() =>
                    validateFormFieldsAndSubmit({
                      formikBag,
                      formFields,
                    })
                  }
                  type="button"
                />
              </SrStaticFooter>
            )}
          </Form>
        );
      }}
    />
  );
};

SrPartnerBankAccount.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  stripeAccount: PropTypes.object,
  listing: PropTypes.object,
  partner: PropTypes.object,
  pendingStripeCharge: PropTypes.bool,
  hideAddBankAccount: PropTypes.bool,
  alternativeMethodEnabled: PropTypes.bool,
  whoCollectsSecurityDepositPetFees: PropTypes.string,
  securityDepositCollectionInstructions: PropTypes.string,
};

SrPartnerBankAccount.defaultProps = {
  hideAddBankAccount: false,
};

SrPartnerBankAccount.displayName = 'SrPartnerBankAccount';

export default SrPartnerBankAccount;
